import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import { BLOGS } from "../data/blogs";
import NewsletterCTA from "../components/NewsletterCTA";
import BlogCover from "../components/BlogCover";

const TAGS = ["All", ...Array.from(new Set(BLOGS.map((b) => b.tag)))];

export default function BlogList() {
  const [tag, setTag] = useState("All");
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    return BLOGS.filter(
      (b) =>
        (tag === "All" || b.tag === tag) &&
        (q.trim() === "" ||
          b.title.toLowerCase().includes(q.toLowerCase()) ||
          b.dek.toLowerCase().includes(q.toLowerCase())),
    );
  }, [tag, q]);

  return (
    <main data-testid="page-blog-list">
      {/* Header */}
      <section className="border-b-2 border-jet bg-cream p-8 pt-[128px] md:p-14 md:pt-[150px] lg:p-20 lg:pt-[160px]">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="font-sans text-xs font-extrabold uppercase tracking-[0.22em] text-cobalt">
              Milet Journal
            </p>
            <h1 className="mt-3 font-sans text-5xl font-extrabold leading-[1.02] tracking-tight md:text-7xl lg:text-[5.5rem]">
              Read it <span className="text-cobalt">raw.</span>
            </h1>
            <p className="mt-6 max-w-2xl font-sans text-lg font-semibold leading-snug text-jet md:text-xl">
              Field notes, nutrition breakdowns, and opinions we can back up.
            </p>
          </div>
          <div className="relative w-full max-w-sm">
            <Search size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2" />
            <input
              data-testid="blog-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search the journal…"
              className="w-full rounded-full border-2 border-jet bg-surface py-3 pl-11 pr-4 font-sans font-semibold outline-none"
              style={{ boxShadow: "4px 4px 0 #0B0B0F" }}
            />
          </div>
        </div>
        <div className="mt-8 flex flex-wrap gap-2">
          {TAGS.map((t) => (
            <button
              key={t}
              data-testid={`blog-filter-${t.toLowerCase()}`}
              onClick={() => setTag(t)}
              className={`rounded-full border-2 border-jet px-4 py-1.5 font-sans text-xs font-extrabold uppercase tracking-[0.15em] transition-all ${
                tag === t
                  ? "bg-cobalt text-cream"
                  : "bg-cream text-jet hover:bg-butter"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </section>

      {/* Grid */}
      <section className="border-b-2 border-jet bg-surface p-6 md:p-14 lg:p-20">
        {filtered.length === 0 ? (
          <p className="py-20 text-center font-sans text-2xl font-semibold text-jet">
            Nothing found. Try another tag.
          </p>
        ) : (
          <div className="mx-auto grid max-w-7xl gap-6 md:grid-cols-2 lg:grid-cols-3">
            {filtered.map((b) => (
              <Link
                key={b.slug}
                to={`/blog/${b.slug}`}
                data-testid={`blog-card-${b.slug}`}
                className="group flex flex-col overflow-hidden border-2 border-jet bg-cream shadow-pop transition-all hover:-translate-y-1 hover:shadow-pop-lg"
              >
                <div className="relative aspect-[4/3] overflow-hidden border-b-2 border-jet">
                  <BlogCover blog={b} />
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <h2 className="font-sans text-xl font-extrabold leading-tight tracking-tight">
                    {b.title}
                  </h2>
                  <p className="mt-2 font-sans text-sm text-jet/80">{b.dek}</p>
                  <p className="mt-4 font-sans text-xs font-extrabold uppercase tracking-[0.18em] text-cobalt">
                    {b.readTime} →
                  </p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Newsletter — shared orange section */}
      <NewsletterCTA source="blog-list" id="newsletter" />
    </main>
  );
}
